import { getApiUrl } from "@/utils/api";
import { resolveImageUrl } from "@/utils/imageUrl";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";

const API = getApiUrl();

const DEFAULT_STYLES = {
  cardBackground: "#e8f0e8",
  cardBorder: "#c5d9c5",
  primaryHex: "#2f4f4f",
  backgroundHex: "#f5f2ed",
  accentHex: "#e07b5d",
  headingAccentHex: "#e07b5d",
  textHex: "#1a1a1a",
  mutedTextHex: "#666666",
  borderHex: "#e5e0d8",
  headerBgHex: "#ffffff",
  footerBgHex: "#2f4f4f",
  buttonBgHex: "#2f4f4f",
  buttonTextHex: "#ffffff",
  heroOverlayOpacity: 50,
  heroImageOpacity: 100,
  heroTextOverlayOpacity: 0,
  heroTextOverlayColor: "#000000",
  heroFontHex: "#ffffff",
  // Hero Text Settings
  heroTitle: "Find Your Dream Home",
  heroSubtitle: "Welcome to SellLocalHomes",
  heroDescription: "Discover exceptional properties in prime locations. Your perfect home is waiting.",
  heroTitleSize: 48,
  heroSubtitleSize: 14,
  heroDescriptionSize: 18,
  heroTextAlign: "left",
  heroHeight: 80,
  heroImage: "", // Uploaded hero image path (relative), falls back to default if empty
  // Navigation settings
  navBgHex: "#ffffff",
  navTextHex: "#1a1a1a",
  navHoverHex: "#2f4f4f",
  navFontSize: 14,
  navLogoFontSize: 20,
  navButtonBgHex: "#2f4f4f",
  navButtonTextHex: "#ffffff",
  navLogoText: "SellLocalHomes",
  navLinks: [
    { name: "Home", path: "/", enabled: true },
    { name: "Properties", path: "/properties", enabled: true },
    { name: "Join Us", path: "/join-us", enabled: true },
    { name: "Contact", path: "/contact", enabled: true }
  ],
  navCtaText: "Request Viewing",
  navCtaEnabled: true,
  // Feature flags — System Settings "Features" section (sitewide kill switches)
  dashboardEnabled: true,
  buyingSectionEnabled: true,
  addPropertyEnabled: true,
  ctaEditEnabled: true,
  ctaViewEnabled: true,
  ctaOffersEnabled: true,
  ctaEnquiriesEnabled: true,
  ctaOrdersEnabled: true,
  ctaDocumentsEnabled: true,
  ctaInfoEnabled: true,
  ctaBuyingEnabled: true,
  // Global image styling — off by default (0 = no rounding / no shadow) so
  // existing sites look unchanged until an admin opts in via Site Styles.
  imageBorderRadius: 0,
  imageShadowIntensity: 0,
  // Page content "placeholder" width (% of viewport) per breakpoint —
  // see .slh-page-placeholder in App.css for where this is consumed.
  pageWidthDesktop: 90,
  pageWidthTablet: 95,
  pageWidthMobile: 100,
  // Global modal width cap (desktop only, % of viewport) — see
  // .slh-dialog-content in App.css.
  modalWidthPercent: 50,
};

// Maps the 0-3 "shadow intensity" slider to an actual box-shadow value.
const SHADOW_PRESETS = [
  "none",
  "0 2px 8px rgba(0,0,0,0.08)",
  "0 4px 16px rgba(0,0,0,0.15)",
  "0 8px 30px rgba(0,0,0,0.25)",
];

// Convert hex to HSL for CSS variables
const hexToHsl = (hex) => {
  if (!hex) return null;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const SiteStylesContext = createContext({ styles: DEFAULT_STYLES });

// Captured once on first applyStyles() call — the original favicon href
// baked into index.html, so clearing a custom faviconUrl can restore it.
let defaultFaviconHref = null;

export const SiteStylesProvider = ({ children }) => {
  const [styles, setStyles] = useState(DEFAULT_STYLES);

  useEffect(() => {
    fetchStyles();
  }, []);

  const fetchStyles = async () => {
    try {
      const response = await axios.get(`${API}/site-styles`);
      const newStyles = { ...DEFAULT_STYLES, ...response.data };
      setStyles(newStyles);
      applyStyles(newStyles);
    } catch (error) {
      console.error("Failed to fetch site styles:", error);
      applyStyles(DEFAULT_STYLES);
    }
  };

  const applyStyles = (s) => {
    const root = document.documentElement;
    
    // Apply CSS custom properties using hex to HSL conversion
    // Button colours drive shadcn <Button>'s `--primary`/`--primary-foreground`
    // — these are the fields literally labelled "Button Background/Text" in
    // Site Styles, so every CTA across the site (not just nav/hero, which
    // have their own dedicated overrides) follows them. Falls back to the
    // legacy "Primary" field only if Button Background was never set.
    const ctaBg = s.buttonBgHex || s.primaryHex;
    if (ctaBg) {
      const hsl = hexToHsl(ctaBg);
      if (hsl) root.style.setProperty('--primary', hsl);
    }
    if (s.buttonTextHex) {
      const hsl = hexToHsl(s.buttonTextHex);
      if (hsl) root.style.setProperty('--primary-foreground', hsl);
    }
    if (s.backgroundHex) {
      const hsl = hexToHsl(s.backgroundHex);
      if (hsl) root.style.setProperty('--background', hsl);
    }
    if (s.accentHex) {
      const hsl = hexToHsl(s.accentHex);
      if (hsl) root.style.setProperty('--accent', hsl);
    }
    if (s.textHex) {
      const hsl = hexToHsl(s.textHex);
      if (hsl) root.style.setProperty('--foreground', hsl);
    }
    if (s.mutedTextHex) {
      const hsl = hexToHsl(s.mutedTextHex);
      if (hsl) root.style.setProperty('--muted-foreground', hsl);
    }
    if (s.borderHex) {
      const hsl = hexToHsl(s.borderHex);
      if (hsl) {
        root.style.setProperty('--border', hsl);
        root.style.setProperty('--input', hsl);
      }
    }
    
    // Apply direct hex values for components that use inline styles
    if (s.headerBgHex || s.navBgHex) root.style.setProperty('--header-bg', s.headerBgHex || s.navBgHex);
    if (s.headerTextHex) root.style.setProperty('--header-text', s.headerTextHex);
    if (s.headerLinkHex) root.style.setProperty('--header-link', s.headerLinkHex);
    if (s.headerLinkHoverHex) root.style.setProperty('--header-link-hover', s.headerLinkHoverHex);
    if (s.headerBorderHex) root.style.setProperty('--header-border', s.headerBorderHex);
    if (s.footerBgHex) root.style.setProperty('--footer-bg', s.footerBgHex);
    if (s.footerTextHex) root.style.setProperty('--footer-text', s.footerTextHex);
    if (s.footerLinkHex) root.style.setProperty('--footer-link', s.footerLinkHex);
    if (s.buttonBgHex) root.style.setProperty('--button-bg', s.buttonBgHex);
    if (s.buttonTextHex) root.style.setProperty('--button-text', s.buttonTextHex);
    if (s.navBgHex) root.style.setProperty('--nav-bg', s.navBgHex);
    if (s.navTextHex) root.style.setProperty('--nav-text', s.navTextHex);
    if (s.navHoverHex) root.style.setProperty('--nav-hover', s.navHoverHex);
    if (s.headingHex) root.style.setProperty('--heading-color', s.headingHex);
    if (s.headingAccentHex) root.style.setProperty('--heading-accent-color', s.headingAccentHex);
    if (s.linkHex) root.style.setProperty('--link-color', s.linkHex);
    if (s.linkHoverHex) root.style.setProperty('--link-hover-color', s.linkHoverHex);
    if (s.sectionBgHex) root.style.setProperty('--section-bg', s.sectionBgHex);
    if (s.cardBgHex) root.style.setProperty('--card-bg', s.cardBgHex);
    if (s.secondaryHex) root.style.setProperty('--secondary-color', s.secondaryHex);

    // Typography: font families applied as CSS variables
    if (s.headingFont) root.style.setProperty('--font-heading', `'${s.headingFont}', serif`);
    else root.style.removeProperty('--font-heading');
    if (s.bodyFont) root.style.setProperty('--font-body', `'${s.bodyFont}', sans-serif`);
    else root.style.removeProperty('--font-body');
    if (s.navFont) root.style.setProperty('--font-nav', `'${s.navFont}', sans-serif`);
    else root.style.removeProperty('--font-nav');
    if (s.listFont) root.style.setProperty('--font-list', `'${s.listFont}', sans-serif`);
    else root.style.removeProperty('--font-list');
    if (s.baseFontSize) root.style.setProperty('--base-font-size', `${s.baseFontSize}px`);
    if (s.listFontSize) root.style.setProperty('--list-font-size', `${s.listFontSize}px`);
    if (s.h1Size) root.style.setProperty('--h1-size', `${s.h1Size}px`);
    if (s.h2Size) root.style.setProperty('--h2-size', `${s.h2Size}px`);
    if (s.h3Size) root.style.setProperty('--h3-size', `${s.h3Size}px`);
    // H4/H5/H6 are derived from a sensible default scale unless an admin
    // explicitly overrides them in Site Styles. Modules pick from H1–H6
    // via the `headingSize` utility, so these vars must always be set.
    root.style.setProperty('--h4-size', `${s.h4Size || 20}px`);
    root.style.setProperty('--h5-size', `${s.h5Size || 18}px`);
    root.style.setProperty('--h6-size', `${s.h6Size || 16}px`);
    // Checkout heading — a dedicated var so admins can tune the size on the
    // basket / checkout pages without touching the global heading scale.
    root.style.setProperty('--checkout-heading-size', `${s.checkoutHeadingSize || 30}px`);
    if (s.headingWeight) root.style.setProperty('--heading-weight', s.headingWeight);
    if (s.bodyWeight) root.style.setProperty('--body-weight', s.bodyWeight);
    if (s.bodyLineHeight) root.style.setProperty('--body-line-height', s.bodyLineHeight);
    if (s.headingLineHeight) root.style.setProperty('--heading-line-height', s.headingLineHeight);
    root.style.setProperty('--heading-spacing', `${s.headingSpacing ?? 16}px`);
    if (s.containerMaxWidth) root.style.setProperty('--container-max-w', `${s.containerMaxWidth}px`);
    // Page content "placeholder" width — the shared container every
    // non-full-bleed module/page targets, responsive per breakpoint.
    root.style.setProperty('--slh-page-width-mobile', `${s.pageWidthMobile ?? 100}%`);
    root.style.setProperty('--slh-page-width-tablet', `${s.pageWidthTablet ?? 95}%`);
    root.style.setProperty('--slh-page-width-desktop', `${s.pageWidthDesktop ?? 90}%`);
    // Global modal width cap — desktop only, % of viewport (see .slh-dialog-content in App.css).
    root.style.setProperty('--slh-modal-max-width', `${s.modalWidthPercent ?? 50}%`);
    // Global image styling (rounded corners + shadow) — public site only.
    root.style.setProperty('--img-border-radius', `${s.imageBorderRadius || 0}px`);
    root.style.setProperty('--img-box-shadow', SHADOW_PRESETS[s.imageShadowIntensity || 0] || "none");

    // Dynamic favicon — reuses the static <link rel="icon"> already in
    // index.html so there's only ever one. Remembers its original href
    // once so clearing faviconUrl (via the Icon Generator's "Reset to
    // default" action) restores it instead of leaving a stale custom one.
    let favLink = document.querySelector("link[rel~='icon']");
    if (!favLink) {
      favLink = document.createElement("link");
      favLink.rel = "icon";
      document.head.appendChild(favLink);
    }
    if (defaultFaviconHref === null) defaultFaviconHref = favLink.href;
    favLink.href = s.faviconUrl ? resolveImageUrl(s.faviconUrl) : defaultFaviconHref;

    // Inject Google Fonts if configured
    const fonts = [s.headingFont, s.bodyFont, s.navFont, s.listFont].filter(Boolean);
    if (fonts.length > 0) {
      const familiesParam = fonts.map(f => f.replace(/ /g, "+") + ":wght@300;400;500;600;700;800").join("&family=");
      const linkId = "site-styles-google-fonts";
      let link = document.getElementById(linkId);
      if (!link) {
        link = document.createElement("link");
        link.id = linkId;
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
      link.href = `https://fonts.googleapis.com/css2?family=${familiesParam}&display=swap`;
    }

    // Inject global stylesheet that makes the entire site comply with these styles.
    // This stylesheet is scoped to elements OUTSIDE the admin dashboard (`[data-public-site]`)
    // so the admin panel keeps its own design system.
    const styleId = "site-styles-global-overrides";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `
      [data-public-site] {
        background-color: ${s.backgroundHex || "#f5f2ed"};
        color: ${s.textHex || "#1a1a1a"};
        font-family: var(--font-body, 'Inter', sans-serif);
        font-size: ${s.baseFontSize || 16}px;
        font-weight: ${s.bodyWeight || 400};
        line-height: ${s.bodyLineHeight || 1.6};
      }
      /* HTML defaults for form controls, buttons and links do not inherit
         font-family from ancestors — modern browsers stamp them with the
         UA's system font. Explicitly re-inherit inside the public site so
         every button, input, link, textarea, select actually respects
         Site Styles fonts. Same treatment for size and weight so an admin
         changing base font in one place cascades everywhere. */
      [data-public-site] button,
      [data-public-site] input,
      [data-public-site] select,
      [data-public-site] textarea,
      [data-public-site] a,
      [data-public-site] span,
      [data-public-site] label,
      [data-public-site] li,
      [data-public-site] td,
      [data-public-site] th,
      [data-public-site] p {
        font-family: inherit;
      }
      [data-public-site] h1, [data-public-site] h2, [data-public-site] h3,
      [data-public-site] h4, [data-public-site] h5, [data-public-site] h6 {
        font-family: var(--font-heading, 'Playfair Display', serif);
        color: ${s.headingHex || "#1a1a1a"};
        font-weight: ${s.headingWeight || 700};
        line-height: ${s.headingLineHeight || 1.2};
        margin-bottom: var(--heading-spacing, 16px);
      }
      [data-public-site] h1 { font-size: ${s.h1Size || 48}px; }
      [data-public-site] h2 { font-size: ${s.h2Size || 36}px; }
      [data-public-site] h3 { font-size: ${s.h3Size || 24}px; }
      [data-public-site] h4 { font-size: ${s.h4Size || 20}px; }
      [data-public-site] h5 { font-size: ${s.h5Size || 18}px; }
      [data-public-site] h6 { font-size: ${s.h6Size || 16}px; }
      /* Bulleted/numbered lists — default to the List Font if an admin set
         one, otherwise just fall through to Normal Font (inherited above). */
      [data-public-site] ul, [data-public-site] ol, [data-public-site] li {
        font-family: var(--font-list, inherit);
        font-size: var(--list-font-size, inherit);
      }
      /* Global image styling (rounded corners + shadow) — logos, avatars
         and icons opt out via the .slh-no-img-style marker class. Applied
         to the img itself AND its nearest cropping wrapper (the common
         overflow-hidden container pattern used by ProgressiveImg/
         PropertyImage/property cards) so rounded corners actually clip
         instead of revealing the wrapper's placeholder colour underneath. */
      [data-public-site] img:not(.slh-no-img-style) {
        border-radius: var(--img-border-radius, 0px);
        box-shadow: var(--img-box-shadow, none);
      }
      [data-public-site] .overflow-hidden:has(> img:not(.slh-no-img-style)) {
        border-radius: var(--img-border-radius, 0px);
        box-shadow: var(--img-box-shadow, none);
      }
      [data-public-site] a:not(.no-global-style):not(header a):not(nav a):not(footer a) {
        color: ${s.linkHex || "#2f4f4f"};
        transition: color 0.15s ease;
      }
      [data-public-site] a:not(.no-global-style):not(header a):not(nav a):not(footer a):hover {
        color: ${s.linkHoverHex || "#1a3030"};
      }
      [data-public-site] header {
        background-color: ${s.headerBgHex || s.navBgHex || "#ffffff"};
        border-color: ${s.headerBorderHex || "transparent"};
        font-family: var(--font-body, 'Inter', sans-serif);
      }
      [data-public-site] header,
      [data-public-site] header span {
        color: ${s.headerTextHex || s.navTextHex || "#1a1a1a"};
      }
      [data-public-site] header a,
      [data-public-site] header button {
        color: ${s.headerLinkHex || s.headerTextHex || s.navTextHex || "#1a1a1a"};
        font-family: inherit;
        transition: color 0.15s ease;
      }
      [data-public-site] header a:hover {
        color: ${s.headerLinkHoverHex || s.navHoverHex || "#2f4f4f"};
      }
      /* Header brand/logo text — pick the heading font so it echoes the
         hero and section titles. Inline styles from HeaderModule still
         win when the admin picks a per-instance font. */
      [data-public-site] header .brand,
      [data-public-site] header [data-role="brand"] {
        font-family: var(--font-heading, 'Playfair Display', serif);
      }
      [data-public-site] nav.public-nav {
        background-color: ${s.navBgHex || "#ffffff"};
        font-family: var(--font-nav, var(--font-body, 'Inter', sans-serif));
      }
      [data-public-site] nav.public-nav a,
      [data-public-site] nav.public-nav button {
        color: ${s.navTextHex || "#1a1a1a"};
        font-family: var(--font-nav, var(--font-body, 'Inter', sans-serif));
      }
      [data-public-site] nav.public-nav a:hover {
        color: ${s.navHoverHex || "#2f4f4f"};
      }
      [data-public-site] footer {
        background-color: ${s.footerBgHex || "#2f4f4f"};
        color: ${s.footerTextHex || "#ffffff"};
        font-family: var(--font-body, 'Inter', sans-serif);
      }
      [data-public-site] footer h1, [data-public-site] footer h2,
      [data-public-site] footer h3, [data-public-site] footer h4 {
        color: ${s.footerTextHex || "#ffffff"};
        font-family: var(--font-heading, 'Playfair Display', serif);
      }
      [data-public-site] footer a,
      [data-public-site] footer button {
        color: ${s.footerLinkHex || s.footerTextHex || "#ffffff"};
        font-family: inherit;
      }
      [data-public-site] footer a:hover { opacity: 0.8; }
    `;
  };

  const updateStyles = async (newStyles) => {
    setStyles(newStyles);
    applyStyles(newStyles);
  };

  return (
    <SiteStylesContext.Provider value={{ styles, updateStyles, refetch: fetchStyles }}>
      {children}
    </SiteStylesContext.Provider>
  );
};

export const useSiteStyles = () => {
  return useContext(SiteStylesContext);
};

export default useSiteStyles;
